import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Plus, X, Check } from 'lucide-react';

const API = '/api';

/**
 * Per-TA disease-area picker.
 *
 * - `therapeuticArea` is the parent TA (e.g., "Ophthalmology")
 * - `value` is a string[] of disease areas already selected for that TA
 * - On open, fetches `/api/disease-areas?ta=<therapeuticArea>` which returns
 *   the curated taxonomy + any custom additions other users have made.
 *
 * UX: small inline pills + a tiny "+ Add disease area" trigger that opens
 * an autocomplete with click-to-toggle and free-text "Add" support — same
 * pattern as the parent TherapeuticAreaSelect for muscle-memory consistency.
 */
export function DiseaseAreaSelect({ therapeuticArea, value = [], onChange, testId }) {
  const tid = testId || `disease-area-select-${(therapeuticArea || 'untyped').toLowerCase().replace(/\s+/g, '-')}`;
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (open && therapeuticArea) {
      axios
        .get(`${API}/disease-areas`, { params: { ta: therapeuticArea }, withCredentials: true })
        .then(res => setOptions(res.data?.disease_areas || []))
        .catch(() => setOptions([]));
    }
  }, [open, therapeuticArea]);

  const toggle = (d) => {
    if (value.includes(d)) {
      onChange(value.filter(v => v !== d));
    } else {
      onChange([...value, d]);
    }
  };

  const addCustom = () => {
    const t = inputValue.trim();
    if (t && !value.includes(t)) {
      onChange([...value, t]);
      setOptions(prev => [...new Set([...prev, t])].sort((a, b) => a.localeCompare(b)));
    }
    setInputValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustom();
    }
  };

  const filtered = options.filter(o =>
    !inputValue.trim() || o.toLowerCase().includes(inputValue.trim().toLowerCase())
  );
  const isNew = inputValue.trim() && !options.some(o => o.toLowerCase() === inputValue.trim().toLowerCase());

  return (
    <div data-testid={tid} className="ml-1 mt-1">
      <div className="flex flex-wrap gap-1 items-center">
        {value.map(d => (
          <Badge
            key={d}
            variant="outline"
            className="text-[10px] px-2 py-0.5 gap-1 bg-white text-slate-600 border-slate-300"
          >
            {d}
            <button
              type="button"
              onClick={() => onChange(value.filter(v => v !== d))}
              className="ml-0.5 hover:text-rose-500"
              data-testid={`${tid}-remove-${d.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <X className="w-2.5 h-2.5" />
            </button>
          </Badge>
        ))}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-[10px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-dashed border-slate-300 hover:border-slate-400 transition-colors"
              data-testid={`${tid}-trigger`}
            >
              <Plus className="w-2.5 h-2.5" /> Disease area
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-0" align="start">
            <div className="px-2 py-1.5 border-b border-slate-100 bg-slate-50/50">
              <p className="text-[9px] uppercase tracking-wider text-slate-400">{therapeuticArea}</p>
            </div>
            <div className="p-2 border-b border-slate-100">
              <div className="flex gap-1.5">
                <Input
                  placeholder="Search or type new disease area…"
                  className="h-7 text-xs"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  data-testid={`${tid}-input`}
                />
              </div>
            </div>
            <div className="max-h-[220px] overflow-y-auto p-1">
              {isNew && (
                <button
                  type="button"
                  className="flex items-center gap-2 w-full px-2 py-1.5 text-xs text-violet-700 font-medium hover:bg-violet-50 rounded"
                  onClick={addCustom}
                  data-testid={`${tid}-add-custom`}
                >
                  <Plus className="w-3 h-3" /> Add "{inputValue.trim()}"
                </button>
              )}
              {filtered.length === 0 && !isNew && (
                <p className="text-[11px] text-slate-400 text-center py-3">No disease areas found</p>
              )}
              {filtered.map(d => {
                const selected = value.includes(d);
                return (
                  <button
                    type="button"
                    key={d}
                    onClick={() => toggle(d)}
                    className={`flex items-center gap-2 w-full px-2 py-1.5 text-xs rounded ${selected ? 'bg-slate-100 text-slate-500' : 'hover:bg-slate-50 text-slate-700'}`}
                  >
                    {selected && <Check className="w-3 h-3 text-emerald-500 shrink-0" />}
                    <span className={selected ? 'line-through' : ''}>{d}</span>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
